import { ReactNode, useCallback, memo, useState } from 'react'
import { Box, Flex, useDisclosure, useMediaQuery } from '@chakra-ui/react'
import { useAuthStore } from '@/store/auth'
import { useNavigate } from 'react-router-dom'
import { Header } from '@/components/layout/Header'
import { Sidebar } from '@/components/layout/Sidebar'
import { useThemeColors } from '@/hooks/useThemeColors'

interface AdminLayoutProps {
  children: ReactNode
}

function AdminLayoutComponent({ children }: AdminLayoutProps) {
  const bgColor = useThemeColors().bg

  // Mobile drawer (overlay); desktop = collapse state
  const mobileDrawer = useDisclosure()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMobile] = useMediaQuery('(max-width: 768px)')

  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()

  const handleLogout = useCallback(async () => {
    await logout()
    navigate('/login')
  }, [logout, navigate])

  const handleSidebarClose = useCallback(() => {
    mobileDrawer.onClose()
  }, [mobileDrawer])

  const handleSidebarOpen = useCallback(() => {
    if (isMobile) {
      mobileDrawer.onOpen()
    } else {
      setIsCollapsed((prev) => !prev)
    }
  }, [isMobile, mobileDrawer])

  return (
    <Box minH="100vh" bg={bgColor}>
      <Flex>
        <Sidebar
          isOpen={mobileDrawer.isOpen}
          onClose={handleSidebarClose}
          isCollapsed={isCollapsed}
        />

        <Box flex="1" bg={bgColor}>
          <Header onOpen={handleSidebarOpen} user={user} onLogout={handleLogout} />

          <Box bg={bgColor} h="calc(100vh - 85px)" maxH="calc(100vh - 85px)" overflow="hidden" position="relative">
            {children}
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}

// Memoize AdminLayout to prevent unnecessary re-renders
export const AdminLayout = memo(AdminLayoutComponent)

